import { useEffect, useId, useMemo, useState } from 'react'
import type { MemberListItem } from '@ai-meeting/shared'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import { ChevronDownIcon } from '@/components/Icons'
import {
  COMBOBOX_CHEVRON_CLASS,
  COMBOBOX_LABEL_CLASS,
  COMBOBOX_LIST_CLASS,
  COMBOBOX_NOTICE_CLASS,
  COMBOBOX_PANEL_CLASS,
  COMBOBOX_PLACEHOLDER_CLASS,
  COMBOBOX_SEARCH_CLASS,
  COMBOBOX_SEARCH_WRAP_CLASS,
  COMBOBOX_TRIGGER_CLASS,
  COMBOBOX_VALUE_CLASS,
} from '@/components/comboboxStyles'
import { useComboboxPanel } from '@/hooks/useComboboxPanel'
import { fetchAllMembers } from '@/services/users'
import { useStore } from '@/store/useStore'
import { normalizeMemberName } from '@/pages/Meetings/utils/normalizeMemberName'
import FieldTooltip from './FieldTooltip'

/** Matches the debounce `ShareMeetingFlow` uses, so both pickers feel the same. */
const SEARCH_DEBOUNCE_MS = 300
/** How many names the closed control spells out before it switches to "+N". */
const SUMMARY_NAME_LIMIT = 2

export interface ShareWithMembersFieldProps {
  selected: MemberListItem[]
  onChange: (next: MemberListItem[]) => void
  /** True while the meeting is being created — the whole field stops accepting input. */
  disabled?: boolean
  /**
   * Normalized display names already in the live shared room (BRAI-785).
   * Matching directory rows stay visible but cannot be selected ("In this room").
   * Omit on Create Meeting — there is no room roster yet.
   */
  inRoomNames?: ReadonlySet<string>
  /** Label shown on disabled in-room rows; defaults to empty (Create Meeting). */
  inRoomLabel?: string
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

/**
 * Picks the BrSE.ai members a meeting is shared with at creation time (BRAI-533).
 *
 * WHY A COMBOBOX AND NOT AN INLINE LIST WITH CHIPS. Both halves of that design
 * grow without bound: the list pushes the create form taller the more people
 * the directory holds, and a chip row wraps to as many lines as there are
 * picks. Here the closed control is one truncated line whatever the count, and
 * the options live in a panel that scrolls inside its own fixed height — so
 * nothing about the modal's layout depends on how many members exist or how
 * many are chosen.
 *
 * Where the panel is drawn, and why it has to be `position: fixed`, lives in
 * {@link useComboboxPanel} — shared with `Combobox` so the two panels in this
 * one form cannot drift apart.
 *
 * WHY `/api/users/members` AND NOT `/shareable-users`. The meeting does not
 * exist yet while this form is open, and `shareable-users` is scoped to a
 * meeting id. The member directory is the only list available this early; the
 * backend still rejects anyone who may not receive the share when the share is
 * actually created.
 *
 * Selection lives in the parent so a failed create keeps it — losing a
 * carefully picked list to one API error is the thing this avoids.
 */
export default function ShareWithMembersField({
  selected,
  onChange,
  disabled = false,
  inRoomNames,
  inRoomLabel,
}: ShareWithMembersFieldProps) {
  const { t } = useUiStrings()
  const labelId = useId()
  const hintId = useId()
  const listboxId = useId()
  const currentEmail = useStore((s) => s.user?.email ?? '')

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [members, setMembers] = useState<MemberListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [search])

  const selectedIds = useMemo(() => new Set(selected.map((s) => s.id)), [selected])

  // The signed-in user is always already in their own meeting. Matched on email
  // because the session carries no user id.
  const normalizedEmail = currentEmail.trim().toLowerCase()
  const options = useMemo(() => {
    const visible = members.filter(
      (member) => member.email.trim().toLowerCase() !== normalizedEmail
    )
    // Chosen rows first: with the chips gone, this list is also how somebody is
    // taken back off, and hunting for them among strangers is the slow way.
    return [
      ...visible.filter((member) => selectedIds.has(member.id)),
      ...visible.filter((member) => !selectedIds.has(member.id)),
    ]
  }, [members, normalizedEmail, selectedIds])

  const panel = useComboboxPanel(options.length)

  useEffect(() => {
    if (!panel.open) return undefined
    let ignore = false
    setLoading(true)
    setError(null)
    fetchAllMembers({ search: debouncedSearch.trim() || undefined })
      .then((result) => {
        if (!ignore) setMembers(result)
      })
      .catch((err: unknown) => {
        if (ignore) return
        setMembers([])
        setError(err instanceof Error ? err.message : t.membersLoadError)
      })
      .finally(() => {
        if (!ignore) setLoading(false)
      })
    return () => {
      ignore = true
    }
  }, [panel.open, debouncedSearch, t.membersLoadError])

  function isInRoom(member: MemberListItem): boolean {
    return Boolean(inRoomNames?.has(normalizeMemberName(member.name)))
  }

  function toggle(member: MemberListItem) {
    if (isInRoom(member)) return
    onChange(
      selectedIds.has(member.id)
        ? selected.filter((s) => s.id !== member.id)
        : [...selected, member]
    )
  }

  function handleSearchKeyDown(event: React.KeyboardEvent) {
    if (panel.handleNavigationKey(event)) return
    if (event.key === 'Enter') {
      event.preventDefault()
      const member = options[panel.activeIndex]
      // Stays open on purpose, unlike the single-choice panel: picking several
      // people is the normal case here, and reopening between each one is the
      // slow way to do it.
      if (member && !isInRoom(member)) toggle(member)
    }
  }

  /**
   * One line, whatever the count. Two names are spelled out; past that the rest
   * collapse into "+N" so the control can never grow or wrap.
   */
  const summary =
    selected.length === 0
      ? t.shareWithMembersPlaceholder
      : selected.length <= SUMMARY_NAME_LIMIT
        ? selected.map((member) => member.name).join(', ')
        : fmt(t.shareWithMembersSummary, {
            names: selected
              .slice(0, SUMMARY_NAME_LIMIT)
              .map((member) => member.name)
              .join(', '),
            count: selected.length - SUMMARY_NAME_LIMIT,
          })

  return (
    <div ref={panel.rootRef}>
      {/* Keep the tooltip outside the element used by `aria-labelledby`.
          Otherwise its full sentence becomes part of the combobox name and
          two buttons on the page both read as the hint instead of one reading
          as Share with. */}
      <div className="flex items-start">
        <p id={labelId} className={COMBOBOX_LABEL_CLASS}>
          {t.shareWithMembersLabel}
        </p>
        {/* The sentence this replaces sat under the control, and this field
            shares a row with the project and the mode — one line of prose on
            one of three made the row ragged. It remains in the accessibility
            tree for a reader who cannot hover. */}
        <FieldTooltip
          id={hintId}
          accessibleLabel={t.shareWithMembersHint}
          text={t.shareWithMembersHint}
          testId="share-with-hint"
        />
      </div>
      <button
        ref={panel.triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={panel.open}
        aria-controls={panel.open ? listboxId : undefined}
        aria-labelledby={labelId}
        aria-describedby={hintId}
        onClick={() => panel.setOpen(!panel.open)}
        className={COMBOBOX_TRIGGER_CLASS}
      >
        <span
          className={[
            COMBOBOX_VALUE_CLASS,
            selected.length === 0 ? COMBOBOX_PLACEHOLDER_CLASS : '',
          ].join(' ')}
        >
          {summary}
        </span>
        {selected.length > 0 && (
          <span className="shrink-0 rounded-full bg-[var(--accent-light)] px-2 py-0.5 text-xs font-semibold text-[var(--accent)]">
            {selected.length}
          </span>
        )}
        <ChevronDownIcon className={COMBOBOX_CHEVRON_CLASS} aria-hidden />
      </button>

      {panel.open && (
        <div
          style={panel.panelStyle}
          className={COMBOBOX_PANEL_CLASS}
        >
          <div className={COMBOBOX_SEARCH_WRAP_CLASS}>
            <input
              ref={panel.searchRef}
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={t.shareWithMembersSearch}
              aria-label={t.shareWithMembersSearch}
              autoComplete="off"
              className={COMBOBOX_SEARCH_CLASS}
            />
          </div>

          <ul
            id={listboxId}
            role="listbox"
            aria-multiselectable
            aria-label={t.shareWithMembersOpen}
            className={COMBOBOX_LIST_CLASS}
          >
            {loading && options.length === 0 && (
              <li className={COMBOBOX_NOTICE_CLASS}>
                {t.loading}
              </li>
            )}
            {!loading && error && (
              <li
                className="px-3 py-4 text-center text-sm text-[var(--error)]"
                role="alert"
              >
                {error}
              </li>
            )}
            {!loading && !error && options.length === 0 && (
              <li className={COMBOBOX_NOTICE_CLASS}>
                {t.shareWithMembersEmpty}
              </li>
            )}
            {options.map((member, index) => {
              const checked = selectedIds.has(member.id)
              const inRoom = isInRoom(member)
              return (
                <li key={member.id} role="presentation">
                  <button
                    ref={index === panel.activeIndex ? panel.activeRowRef : undefined}
                    type="button"
                    role="option"
                    aria-selected={checked}
                    aria-disabled={inRoom}
                    disabled={inRoom}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => panel.setActiveIndex(index)}
                    onClick={() => toggle(member)}
                    className={[
                      'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors',
                      inRoom ? 'cursor-not-allowed opacity-60' : '',
                      index === panel.activeIndex && !inRoom ? 'bg-[var(--bg-tertiary)]' : '',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        checked
                          ? 'border-[var(--accent)] bg-[var(--accent)] text-white'
                          : 'border-[var(--border-color)] bg-[var(--bg-primary)]',
                      ].join(' ')}
                      aria-hidden
                    >
                      {checked && !inRoom && (
                        <svg viewBox="0 0 16 16" className="h-3 w-3" fill="currentColor">
                          <path d="M6.5 11.5 3 8l1-1 2.5 2.5L12 4l1 1z" />
                        </svg>
                      )}
                    </span>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent-light)] text-xs font-bold text-[var(--accent)]">
                      {initials(member.name)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-[var(--text-primary)]">
                        {member.name}
                      </span>
                      <span className="block truncate text-xs text-[var(--text-secondary)]">
                        {member.email}
                      </span>
                    </span>
                    {inRoom && inRoomLabel ? (
                      <span className="shrink-0 text-xs text-[var(--text-tertiary)]">
                        {inRoomLabel}
                      </span>
                    ) : null}
                  </button>
                </li>
              )
            })}
          </ul>

          {selected.length > 0 && (
            <div className="flex items-center justify-between gap-2 border-t border-[var(--border-color)] px-3 py-2">
              <span className="text-xs text-[var(--text-secondary)]">
                {fmt(t.nSelected, { count: selected.length })}
              </span>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onChange([])}
                className="text-xs font-semibold text-[var(--accent)]"
              >
                {t.shareWithMembersClear}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
